import React, { createContext, useContext, useEffect, useState } from 'react';
import { MsalProvider, useMsal, useIsAuthenticated } from '@azure/msal-react';
import { msalInstance, msalConfig, loginRequest } from '../config/authConfig';

// User interface
export interface User {
  id: string;
  email: string;
  name: string;
  tenantId: string;
  roles: string[];
}

// Auth context interface
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthProviderInner>{children}</AuthProviderInner>
    </MsalProvider>
  );
};

const AuthProviderInner: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { instance, accounts } = useMsal();
  const account = accounts[0] ?? null;
  const accountId = account?.homeAccountId;
  const msalAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      if (import.meta.env.DEV && localStorage.getItem('anchor_auth_bypass') === 'true' && localStorage.getItem('anchor_auth_token')) {
        try {
          const token = localStorage.getItem('anchor_auth_token')!;
          const payload = JSON.parse(atob(token.split('.')[1]));
          const userData: User = {
            id: payload.id || payload.sub || '',
            email: payload.email || '',
            name: payload.name || '',
            tenantId: payload.tenantId || '',
            roles: payload.roles || [],
          };
          setUser(userData);
          setIsLoading(false);
          return;
        } catch (error) {
          console.error('Auth bypass failed:', error);
        }
      }

      try {
        if (account && msalAuthenticated) {
          const userData: User = {
            id: account.localAccountId || account.homeAccountId || '',
            email: account.username || '',
            name: account.name || '',
            tenantId: account.tenantId || '',
            roles: (account.idTokenClaims as any)?.roles || [],
          };
          setUser(userData);
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [accountId, msalAuthenticated]);

  useEffect(() => {
    const handleAuthExpired = () => {
      setUser(null);
    };
    window.addEventListener('anchor:auth-expired', handleAuthExpired);
    return () => window.removeEventListener('anchor:auth-expired', handleAuthExpired);
  }, []);

  const login = async (): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await instance.loginPopup(loginRequest);
      
      if (response.account) {
        const userData: User = {
          id: response.account.localAccountId || response.account.homeAccountId || '',
          email: response.account.username || '',
          name: response.account.name || '',
          tenantId: response.account.tenantId || '',
          roles: (response.account.idTokenClaims as any)?.roles || [],
        };
        setUser(userData);
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      setIsLoading(true);
      await instance.logoutPopup({
        postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri || window.location.origin,
        mainWindowRedirectUri: msalConfig.auth.postLogoutRedirectUri || window.location.origin
      });
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const getAccessToken = async (): Promise<string | null> => {
    // Return bypass token for Playwright E2E testing (dev builds only)
    if (import.meta.env.DEV && localStorage.getItem('anchor_auth_bypass') === 'true') {
      return localStorage.getItem('anchor_auth_token');
    }

    if (!account) {
      return null;
    }

    try {
      // Get ID token via acquireTokenSilent — handles refresh automatically.
      // Only use ID token (audience = our app's clientId). Never fall back to
      // accessToken — it has audience=graph.microsoft.com and the server rejects it.
      const response = await instance.acquireTokenSilent({
        scopes: ['openid', 'profile', 'email'],
        account: account,
      });

      return response.idToken || null;
    } catch {
      // Silent acquisition failed — try interactive popup
      try {
        const response = await instance.acquireTokenPopup({
          scopes: ['openid', 'profile', 'email'],
          account: account,
        });
        return response.idToken || null;
      } catch {
        return null;
      }
    }
  };

  const isAuthenticated = msalAuthenticated || user !== null;

  const contextValue: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    getAccessToken
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;